// Bunn-verktøylinje — GRUPPERT for å fungere på lav oppløsning. I stedet for ~24
// flate knapper vises noen få gruppeknapper; klikk åpner en popover med gruppens
// verktøy (glyph + etikett + evt. hurtigtast). Oppdagbarhet beholdt (alt er
// synlig via grupper), men bredden kuttes drastisk.
export interface DockItem {
  id: string;
  label: string;
  glyph: string;
  key?: string;
  title: string;
  run: () => void;
}

export interface DockGroup {
  id: string;
  label: string;
  glyph: string;
  items: DockItem[];
}

export class ToolDock {
  private el: HTMLElement;
  private openPop: HTMLElement | null = null;
  /** Verktøy-id → knapp + gruppeknapp, slik at tilstand kan speiles etterpå. */
  private items: Record<string, { btn: HTMLElement; group: HTMLElement; count?: HTMLElement }> = {};
  /** Gruppe-element → id-er i gruppa som er aktive (for prikken på gruppeknappen). */
  private activeByGroup = new Map<HTMLElement, Set<string>>();

  constructor(container: HTMLElement, groups: DockGroup[]) {
    this.el = document.createElement('div');
    this.el.className = 'tool-dock';

    for (const g of groups) {
      if (!g.items.length) continue;
      const wrap = document.createElement('div');
      wrap.className = 'dock-group';

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'dock-btn dock-group-btn';
      btn.title = g.label;
      btn.append(glyphEl(g.glyph), labelEl(g.label), caret());

      const pop = document.createElement('div');
      pop.className = 'dock-pop';
      for (const it of g.items) {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'dock-pop-item';
        b.title = it.key ? `${it.title}  (${it.key})` : it.title;
        b.append(glyphEl(it.glyph), labelEl(it.label));
        if (it.key) b.append(keyEl(it.key));
        b.onclick = () => { it.run(); this.close(); };
        pop.appendChild(b);
        this.items[it.id] = { btn: b, group: btn };
      }

      btn.onclick = (e) => { e.stopPropagation(); this.togglePop(pop); };
      wrap.append(btn, pop);
      this.el.appendChild(wrap);
    }

    container.appendChild(this.el);
    // Klikk utenfor lukker åpen popover.
    document.addEventListener('click', () => this.close());
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
  }

  /**
   * Speile et lags AV/PÅ-tilstand på verktøyknappen (samme mønster som
   * sidebarens `.layer-row.off` og `.report-type-btn.active`). Gruppeknappen får
   * `.has-active` så tilstanden er synlig UTEN å åpne popoveren — ellers ser
   * operatøren aldri at laget er på.
   */
  setActive(id: string, on: boolean) {
    const e = this.items[id];
    if (!e) return;
    e.btn.classList.toggle('active', on);
    e.btn.setAttribute('aria-pressed', String(on));
    let set = this.activeByGroup.get(e.group);
    if (!set) { set = new Set(); this.activeByGroup.set(e.group, set); }
    if (on) set.add(id); else set.delete(id);
    e.group.classList.toggle('has-active', set.size > 0);
  }

  /** Antall synlige objekter i laget (samme rolle som `Shell.setCount`).
   *  0 eller negativt fjerner merket. */
  setCount(id: string, n: number) {
    const e = this.items[id];
    if (!e) return;
    if (n <= 0) { e.count?.remove(); e.count = undefined; return; }
    if (!e.count) { e.count = countEl(); e.btn.appendChild(e.count); }
    e.count.textContent = String(n);
  }

  /** Er verktøyknappen markert aktiv? (til test/verifikasjon) */
  isActive(id: string): boolean { return this.items[id]?.btn.classList.contains('active') ?? false; }

  private togglePop(pop: HTMLElement) {
    if (this.openPop === pop) { this.close(); return; }
    this.close();
    pop.classList.add('open');
    this.openPop = pop;
  }
  private close() {
    if (this.openPop) { this.openPop.classList.remove('open'); this.openPop = null; }
  }
}

function glyphEl(g: string): HTMLElement {
  const e = document.createElement('span');
  e.className = 'dock-glyph';
  // Støtt inline-SVG-ikoner (våre egne, klarerte konstanter) i tillegg til tegn.
  if (g.trim().startsWith('<svg')) e.innerHTML = g;
  else e.textContent = g;
  return e;
}
function labelEl(t: string): HTMLElement { const e = document.createElement('span'); e.className = 'dock-label'; e.textContent = t; return e; }
function keyEl(k: string): HTMLElement { const e = document.createElement('span'); e.className = 'dock-key'; e.textContent = k.trim() === '' ? '␣' : k.toUpperCase(); return e; }
/** Ekte SVG-vinkel (chevron), ikke tekst-glyfen «▴» (UX-funn 2026-07-29):
 *  tegn-glyfer arver fontens tilfeldige vekt/grunnlinje og ser ulike ut på
 *  tvers av plattformer — et tegnet ikon er identisk overalt. Peker OPP fordi
 *  popoveren åpner seg OVER docken. */
function caret(): HTMLElement {
  const e = document.createElement('span');
  e.className = 'dock-caret';
  e.setAttribute('aria-hidden', 'true');
  e.innerHTML =
    '<svg viewBox="0 0 10 6" width="9" height="6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M1 5l4-3.6L9 5"/></svg>';
  return e;
}
/** Antallsmerke i verktøyknappen — samme visuelle språk som `.roster-count`. */
function countEl(): HTMLElement { const e = document.createElement('span'); e.className = 'dock-count'; return e; }
